import { SagaIterator } from 'redux-saga';
import { Trans } from 'react-i18next';
import {
  all,
  put,
  call,
  take,
  takeEvery,
  race,
  select,
} from 'redux-saga/effects';
import { URLS, Url } from '@wix/da-url';
import {
  PapiDaSharedTierCreate,
  PapiDaSharedTierDelete,
  PapiDaSharedTierPublish,
  PapiDaSharedTierUpdate,
  PapiRequestDaSharedTierCreate,
  PapiRequestDaSharedTierDelete,
  PapiRequestDaSharedTierPublish,
  PapiRequestDaSharedTierUpdate,
  PapiUser,
} from '@wix/da-papi-types';
import ddt from '@wix/da-ddt';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { Warning as WarningIllustration } from '@wix/da-ds/pkg/Icons/Confirmations';
import { editDeviation } from '../../../redux/deviations/actionCreators';
import { requestPuppy, isApiError } from '@wix/da-http-client';
import { logBiEvent } from '../../../biLogger/redux/saga';
import { DeviationResourceType } from '../../../types/deviation';
import {
  reduxModuleLoaded,
  saveTierAsDraft,
  saveTierAsDraftAndBuyCore,
  publishTier,
  deleteTier,
  updateTier,
  tierSaveError,
  tierSaveRedirect,
  tierSaveRedirectAfterDraft,
  publishDraftTier,
  deleteTierSuccess,
} from './actions';
import { TierPayloadType } from './types';
import {
  pushGlobalConfirmationModal,
  confirmNo,
  confirmYes,
  popModal,
} from '../../../Modals/redux/actionCreators';
import {
  getCurrentUser,
  getCurrentUserHasCore,
} from '../../../publicSession/selectors';
import i18next from 'i18next';
import { CloseOnConfirmOption } from '../../../Popup/ConfirmationModal/types';
import { putErrorNotification } from '../../../utils/saga';

import {
  BiData,
  TierDeleteBiEvent,
  TierPublishBiEvent,
  TierSaveAsDraftBiEvent,
  TierSaveAsDraftConfirmBiEvent,
  TierUpdateBiEvent,
} from '@wix/da-bi/pkg/events';
import s from './tierPopupSaga.scss';

const logger = ddt.createLogger('tierpopup');

export function fakeResponse(options: any): any {
  if (process.env.NODE_ENV !== 'production') {
    if (window.doError) {
      return {
        error: 'invalid_request',
        error_description: 'Not logged in.',
        status: 'error',
      };
    }
    logger.log('faking the response!');
    const { deviationsByType } = require('@wix/da-sample-data'); // eslint-disable-line global-require
    return { ...deviationsByType.tiers[0], ...(options?.data || {}) };
  }
}

function handleSaveTier({
  title,
  description,
  price,
  benefits,
  tags,
  accessSettings,
  matureContent,
  coverImage,
  showSubscriberCount = false,
  messageTitle,
  messageText,
  tierId = undefined,
  discordRoles,
}: TierPayloadType) {
  const isUpdate = !!tierId;
  const url = isUpdate ? '/tier/update' : '/tier/create';
  const method = 'post';
  const isMature = matureContent !== undefined && matureContent !== 'safe';
  let data: PapiRequestDaSharedTierCreate = {
    title,
    description,
    price: parseInt(price || '1'),
    display_num_subscribers: showSubscriberCount,
    benefits,
    tags,
    access_settings: accessSettings,
    thank_message_title: messageTitle,
    thank_message_body: messageText,
    cover_image_deviation_id: coverImage?.deviationId,
    is_mature: isMature,
    mature_level: (isMature ? matureContent : undefined) as any,
    discord_role_ids: discordRoles,
  };

  if (isUpdate) {
    data = {
      ...data,
      tierid: tierId,
    } as PapiRequestDaSharedTierUpdate;
  }

  return call(
    requestPuppy,
    {
      method,
      url,
      data,
    },
    fakeResponse
  );
}

function getCommonBiDataFromPayload(actionPayload) {
  const { deviationId, showSubscriberCount, accessSettings } = actionPayload;
  return {
    itemid: deviationId,
    typeid: deviationId ? DeviationResourceType.TIER : undefined,
    display_subscribers: showSubscriberCount ? 1 : 0,
    access_settings: accessSettings,
  };
}

function* publishTierSaga(
  action: ReturnType<typeof publishTier>
): SagaIterator {
  yield call(
    logBiEvent,
    BiData<TierPublishBiEvent>({
      evid: 535,
      ...getCommonBiDataFromPayload(action.payload),
    })
  );

  let { tierId } = action.payload;
  const afterActionUrl = action.payload.afterActionUrl;
  logger.log('payload', action.payload);

  // Create OR update the tier
  const createOrUpdateResponse:
    | PapiDaSharedTierCreate
    | PapiDaSharedTierUpdate = yield handleSaveTier(action.payload);

  if (!createOrUpdateResponse) {
    yield putErrorNotification(
      i18next.t('tierPopup.error.writeTierServerError')
    );
    yield put(tierSaveError(i18next.t('tierPopup.error.writeTierServerError')));
    return;
  }

  if (isApiError(createOrUpdateResponse)) {
    yield putErrorNotification(createOrUpdateResponse.errorDescription);
    yield put(tierSaveError(createOrUpdateResponse.errorDescription));
    return;
  }

  if (!createOrUpdateResponse?.deviation?.tier?.tierId) {
    yield putErrorNotification(
      i18next.t('tierPopup.error.writeTierServerError')
    );
    yield put(tierSaveError(i18next.t('tierPopup.error.writeTierServerError')));
    return;
  }

  tierId = createOrUpdateResponse.deviation.tier.tierId;

  // Publish the tier
  const data: PapiRequestDaSharedTierPublish = {
    tierid: tierId!,
  };
  const response: PapiDaSharedTierPublish = yield call(requestPuppy, {
    method: 'post',
    url: '/tier/publish',
    data,
  });

  if (!response) {
    yield putErrorNotification(
      i18next.t('tierPopup.error.writeTierServerError')
    );
    yield put(tierSaveError(i18next.t('tierPopup.error.writeTierServerError')));
    return;
  }

  if (isApiError(response)) {
    yield putErrorNotification(response.errorDescription);
    yield put(tierSaveError(response.errorDescription));
    return;
  }

  yield put(tierSaveRedirect());
  const currentUser: PapiUser = yield select(getCurrentUser);
  window.location.href =
    afterActionUrl ??
    `${Url.userSubscriptionsPath({
      username: currentUser.username,
      tierDeviationId: tierId,
    })}?tier-publish-success=1`;
}

function* publishDraftTierSaga(
  action: ReturnType<typeof publishDraftTier>
): SagaIterator {
  yield call(
    logBiEvent,
    BiData<TierPublishBiEvent>({
      evid: 535,
      ...getCommonBiDataFromPayload(action.payload),
    })
  );

  const { deviation, afterActionUrl } = action.payload;
  const { deviationId } = deviation;

  const data: PapiRequestDaSharedTierPublish = {
    tierid: deviationId,
  };
  const response: PapiDaSharedTierPublish = yield call(requestPuppy, {
    method: 'post',
    url: '/tier/publish',
    data,
  });

  if (!response || isApiError(response)) {
    yield put(editDeviation({ deviation }));
    return;
  }

  yield put(tierSaveRedirect());
  const currentUser: PapiUser = yield select(getCurrentUser);
  window.location.href =
    afterActionUrl ??
    `${Url.userSubscriptionsPath({
      username: currentUser.username,
      tierDeviationId: response?.deviation?.deviationId,
    })}?tier-publish-success=1`;
}

function* updateTierSaga(action: ReturnType<typeof updateTier>): SagaIterator {
  yield call(
    logBiEvent,
    BiData<TierUpdateBiEvent>({
      evid: 538,
      ...getCommonBiDataFromPayload(action.payload),
    })
  );

  const response: PapiDaSharedTierUpdate = yield handleSaveTier(action.payload);

  if (!response) {
    yield putErrorNotification(
      i18next.t('tierPopup.error.writeTierServerError')
    );
    yield put(tierSaveError(i18next.t('tierPopup.error.writeTierServerError')));
    return;
  }

  if (isApiError(response)) {
    yield putErrorNotification(
      response.errorDetails || response.errorDescription
    );
    yield put(tierSaveError(response.errorDescription));
    return;
  }

  yield put(tierSaveRedirect());
  window.location.reload();
}

function* saveTierDraftSaga(
  action: ReturnType<typeof saveTierAsDraft>
): SagaIterator {
  const { deviationId, afterActionUrl } = action.payload;
  const currentUserHasCore = yield select(getCurrentUserHasCore);
  yield call(
    logBiEvent,
    BiData<TierSaveAsDraftBiEvent>({
      evid: 539,
      ...getCommonBiDataFromPayload(action.payload),
    })
  );

  if (action.type === saveTierAsDraft.toString()) {
    yield put(
      pushGlobalConfirmationModal({
        titleTransKey: 'tierPopup.saveConfirmation.title',
        textTransKey: currentUserHasCore
          ? 'tierPopup.saveConfirmation.text'
          : 'tierPopup.saveConfirmation.text.nonCore',
        cancelBtnLabelTransKey: 'tierPopup.saveConfirmation.button.cancel',
        confirmBtnLabelTransKey: 'tierPopup.saveConfirmation.button.confirm',
        confirmBtnBiData: BiData<TierSaveAsDraftConfirmBiEvent>({
          evid: 540,
          typeid: deviationId ? DeviationResourceType.TIER : 0,
          itemid: deviationId ?? 0,
          display_subscribers: action.payload.showSubscriberCount ? 1 : 0,
          access_settings: action.payload.accessSettings,
        }),
        icon: <WarningIllustration />,
        closeOnConfirmation: CloseOnConfirmOption.DontClose,
      })
    );

    const { no } = yield race({
      yes: take(confirmYes),
      no: take(confirmNo),
    });

    if (no) {
      yield put(popModal());
      return;
    }
  }

  logger.log('saving', action.payload);
  const response: PapiDaSharedTierCreate = yield handleSaveTier(action.payload);

  if (!response) {
    yield putErrorNotification(
      i18next.t('tierPopup.error.writeTierServerError')
    );
    yield put(tierSaveError(i18next.t('tierPopup.error.writeTierServerError')));
    return;
  }

  if (isApiError(response)) {
    yield putErrorNotification(response.errorDescription);
    yield put(tierSaveError(response.errorDescription));
    return;
  }

  if (action.type === saveTierAsDraft.toString()) {
    yield put(tierSaveRedirectAfterDraft());
    const currentUser: PapiUser = yield select(getCurrentUser);
    requestAnimationFrame(() => {
      window.location.href =
        afterActionUrl ??
        Url.userSubscriptionsPath({
          username: currentUser.username,
        });
    });
  } else if (action.type === saveTierAsDraftAndBuyCore.toString()) {
    yield put(tierSaveRedirectAfterDraft());
    requestAnimationFrame(() => {
      window.location.href = URLS.CORE_MEMBERSHIP;
    });
  }
}

function* deleteTierSaga({
  payload: { deviation, shouldRedirect = true, redirectTo },
}: ReturnType<typeof deleteTier>): SagaIterator {
  const tierid = deviation.tier?.tierId;

  if (!tierid) {
    yield putErrorNotification(
      i18next.t('tierPopup.error.writeTierServerError')
    );
    yield put(tierSaveError(i18next.t('tierPopup.error.writeTierServerError')));
    return;
  }

  const hasSubscribers = (deviation.tier?.stats?.subscribers ?? 0) > 0;

  const isDraft = deviation.tier?.state === 'draft';

  const textTranslationKey = isDraft
    ? 'tierPopup.deleteConfirmation.draft.text'
    : hasSubscribers
      ? 'tierPopup.deleteConfirmation.published.hasSubscribers.text'
      : 'tierPopup.deleteConfirmation.published.noSubscribers.text';

  const textTranslationCmp = () => (
    <div>
      <Trans i18nKey={textTranslationKey}>
        New subscribers won’t be able to join. Content not associated with a
        different tier will become visible to all.
        <PlainButtonOrLink
          href="https://www.deviantartsupport.com/en/article/how-do-i-edit-or-delete-a-tier"
          target="_blank"
          className={s['modal-link']}
        >
          Learn more about deleting tiers
        </PlainButtonOrLink>
      </Trans>
    </div>
  );

  yield put(
    pushGlobalConfirmationModal({
      titleTransKey: isDraft
        ? 'tierPopup.deleteConfirmation.draft.title'
        : 'tierPopup.deleteConfirmation.published.title',
      textComponent: textTranslationCmp,
      cancelBtnLabelTransKey: 'tierPopup.deleteConfirmation.button.cancel',
      confirmBtnLabelTransKey: 'tierPopup.deleteConfirmation.button.confirm',
      confirmBtnBiData: BiData<TierDeleteBiEvent>({
        evid: 541,
        typeid: DeviationResourceType.TIER,
        itemid: deviation.deviationId,
        has_subscribers: hasSubscribers ? 1 : 0,
      }),
      icon: <WarningIllustration />,
      closeOnConfirmation: CloseOnConfirmOption.DontClose,
    })
  );

  const { no } = yield race({
    yes: take(confirmYes),
    no: take(confirmNo),
  });

  if (no) {
    yield put(popModal());
    return;
  }

  logger.log('deleting', tierid);
  const data: PapiRequestDaSharedTierDelete = {
    tierid,
  };
  const response: PapiDaSharedTierDelete = yield call(
    requestPuppy,
    {
      method: 'post',
      url: `/tier/delete`,
      data,
    },
    fakeResponse
  );

  if (!response) {
    yield putErrorNotification(
      i18next.t('tierPopup.error.writeTierServerError')
    );
    yield put(tierSaveError(i18next.t('tierPopup.error.writeTierServerError')));
    return;
  }

  if (isApiError(response)) {
    yield putErrorNotification(response.errorDescription);
    yield put(tierSaveError(response.errorDescription));
    return;
  }

  if (shouldRedirect) {
    yield put(tierSaveRedirect());
    const currentUser: PapiUser = yield select(getCurrentUser);
    window.location.href =
      redirectTo ??
      Url.userSubscriptionsPath({
        username: currentUser.username,
      });
  } else {
    yield put(deleteTierSuccess());
    yield put(popModal());
  }
}

export default function* saga() {
  yield take(reduxModuleLoaded); // wait for redux to load
  yield all([
    takeEvery(saveTierAsDraft, saveTierDraftSaga),
    takeEvery(saveTierAsDraftAndBuyCore, saveTierDraftSaga),
    takeEvery(publishTier, publishTierSaga),
    takeEvery(publishDraftTier, publishDraftTierSaga),
    takeEvery(deleteTier, deleteTierSaga),
    takeEvery(updateTier, updateTierSaga),
  ]);
}
